import { safeLoader } from "@/lib/safe-loader";
import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { InlineCta } from "@/components/site/InlineCta";
import { createFileRoute } from "@tanstack/react-router";
import { resolveSeo, siteContentQuery, useContent } from "@/lib/site-content";
import { Clock, Mail, MapPin, Phone } from "lucide-react";

import { PageHero, Prose } from "../../components/site/Page";
import { SchoolMap } from "@/components/site/SchoolMap";
import { ContactForm } from "@/components/site/ContactForm";

export const Route = createFileRoute("/en/contact-us")({
  loader: async ({ context }) => {
    const data = await safeLoader("en.contact-us.content", () => context.queryClient.ensureQueryData(siteContentQuery), undefined);
    return { seo: resolveSeo(data?.content, "kontakt", "en") };
  },
  head: ({ loaderData }) => {
    const title = loaderData?.seo.title ?? "";
    const description = loaderData?.seo.description ?? "";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: absoluteUrl("/en/contact-us") },
        { name: "twitter:card", content: "summary_large_image" },
        { property: "og:locale", content: "en_GB" },
        ...ogImage(),
      ],
      links: seoLinks("/en/contact-us"),
    };
  },
  component: ContactUs,
});

const CONTACTS = [
  { label: "General enquiries", value: "info@sweintschool.com" },
  { label: "Head teacher", value: "rektor@sweintschool.com" },
  { label: "Jobs and applications", value: "join@sweintschool.com" },
];

function ContactUs() {
  const { text } = useContent("kontakt");

  return (
    <>
      <PageHero
        eyebrow={text("hero.eyebrow")}
        title={text("hero.title")}
        intro={text("hero.intro")}
      />

      <section className="mx-auto max-w-6xl px-5 py-14">
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <MapPin className="size-5 text-primary" />
            <h2 className="mt-3 font-display text-base font-semibold">Visit us</h2>
            <address className="mt-1 text-sm not-italic leading-relaxed text-foreground/90">
              Urb Costalita 1, Cancelada
              <br />
              29680 Estepona, Spain
              <br />
              <span className="text-muted-foreground">Between Marbella and Estepona</span>
            </address>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <Phone className="size-5 text-primary" />
            <h2 className="mt-3 font-display text-base font-semibold">Call us</h2>
            <p className="mt-1 text-sm">
              <a className="hover:text-primary" href="tel:+34607148331">
                +34 607 148 331
              </a>
            </p>
            <p className="mt-1 inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <Clock className="size-3.5" /> Phone hours 15:00–16:00 on weekdays
            </p>
          </div>

          <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
            <Mail className="size-5 text-primary" />
            <h2 className="mt-3 font-display text-base font-semibold">Email us</h2>
            <dl className="mt-1 space-y-1.5 text-sm">
              {CONTACTS.map((c) => (
                <div key={c.value}>
                  <dt className="text-xs text-muted-foreground">{c.label}</dt>
                  <dd>
                    <a className="hover:text-primary" href={`mailto:${c.value}`}>
                      {c.value}
                    </a>
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>

        <InlineCta locale="en" className="mt-8" />

        <div className="mt-10 grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
          <ContactForm locale="en" />

          <SchoolMap locale="en" />
        </div>
      </section>

      <Prose>
        <h2>Come and visit</h2>
        <p>
          The best way to get a feel for the school is to come here. We are happy to show you the
          premises, classrooms and outdoor areas and to explain what an ordinary day looks like. Email
          or call and we'll find a time that suits — preferably during the school day, so you can see
          the school in action.
        </p>

        <h2>How quickly we reply</h2>
        <p>
          Emails are normally answered on weekdays. Phone hours are 15:00–16:00, since staff are with
          the children during the school day. During holidays replies may take longer — see the{" "}
          <a className="text-primary link-quiet" href="/en/school-year-dates">school year dates</a>.
        </p>

        <h2>Finding us</h2>
        <p>
          The school is at Urb Costalita 1 in Cancelada, between Estepona and Marbella, a few minutes
          from the A-7. There is parking near the school. Use the map above for directions.
        </p>

        <h2>Questions about applying</h2>
        <p>
          For questions about starting school, places or fees you are welcome to write directly to the
          principal. If you'd like to get started, the form is under{" "}
          <a className="text-primary link-quiet" href="/en/application">application</a>, and fees
          are on the{" "}
          <a className="text-primary link-quiet" href="/en/fees">fees page</a>.
        </p>
      </Prose>
    </>
  );
}
